import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

interface NavbarProps {
  onOpenModal: () => void;
}

export default function Navbar({ onOpenModal }: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    setMobileOpen(false);
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    } else {
      // Section lives on the homepage, so route there with the hash.
      navigate(`/#${id}`);
    }
  };

  const linkClass =
    'font-semibold text-sm whitespace-nowrap cursor-pointer transition-colors text-primary-500 hover:text-accent-500';

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled ? 'bg-white shadow-sm' : 'bg-white'
      }`}
    >
      <div className="w-full px-4 md:px-6 py-3 flex items-center justify-between gap-4">
        <a href="/" className="flex items-center shrink-0">
          <img
            alt="Gracie & Lily Adventures Logo"
            className={`w-auto object-contain drop-shadow-[0_4px_12px_rgba(0,0,0,0.15)] transition-all duration-300 ${scrolled ? 'h-12 lg:h-14' : 'h-10 lg:h-12'}`}
            src="https://public.readdy.ai/ai/img_res/91ea3fda-8705-4921-ab9c-220fa8418490.png"
          />
        </a>

        <div className="hidden lg:flex flex-1 items-center justify-center">
          <div className="flex flex-col items-center gap-y-1">
            <div className="flex items-center justify-center flex-wrap gap-x-5 gap-y-1">
              <Link to="/about" className={linkClass}>
                About
              </Link>
              <button onClick={() => scrollToSection('books')} className={linkClass}>
                Books
              </button>
              <button onClick={() => scrollToSection('sisters')} className={linkClass}>
                Meet the Sisters
              </button>
              <button onClick={() => scrollToSection('stories')} className={linkClass}>
                Stories
              </button>
              <Link to="/knowledge-base" className={linkClass}>
                Knowledge Base
              </Link>
            </div>
            <div className="flex items-center justify-center flex-wrap gap-x-5 gap-y-1">
              <a
                href="https://gracie-and-lilys-adventures.myshopify.com/"
                target="_blank"
                rel="noopener noreferrer"
                className={`${linkClass} flex items-center gap-1`}
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-store-2-line text-base"></i>
                </span>
                Shop Merchandise
              </a>
              <a
                href="https://a.co/d/0gs915Vo"
                target="_blank"
                rel="noopener noreferrer"
                className={`${linkClass} flex items-center gap-1`}
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-amazon-fill text-base"></i>
                </span>
                Shop on Amazon
              </a>
              <a
                href="https://www.etsy.com/shop/GracieandLilysAdv"
                target="_blank"
                rel="noopener noreferrer"
                className={`${linkClass} flex items-center gap-1`}
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-shopping-basket-2-line text-base"></i>
                </span>
                Shop on Etsy
              </a>
              <a
                href="https://www.facebook.com/profile.php?id=61582630212972"
                target="_blank"
                rel="noopener noreferrer"
                className={`${linkClass} flex items-center gap-1`}
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-facebook-fill text-base"></i>
                </span>
                Facebook
              </a>
            </div>
          </div>
        </div>

        <div className="hidden lg:flex items-center gap-3 shrink-0">
          <button
            onClick={() => onOpenModal()}
            className="bg-accent-500 text-white px-4 py-2 rounded-full font-bold text-sm whitespace-nowrap cursor-pointer hover:bg-accent-600 transition-colors flex items-center gap-1.5"
          >
            <span className="w-5 h-5 flex items-center justify-center">
              <i className="ri-gift-line text-base"></i>
            </span>
            Claim Free Gift
          </button>
          <button
            onClick={() => scrollToSection('books')}
            className="bg-accent-500 text-white px-4 py-2 rounded-full font-bold text-sm whitespace-nowrap cursor-pointer hover:bg-accent-600 transition-colors"
          >
            Shop Books
          </button>
        </div>

        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="lg:hidden w-10 h-10 flex items-center justify-center cursor-pointer text-primary-500"
          aria-label="Toggle menu"
        >
          <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-2xl`}></i>
        </button>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-background-200 px-4 py-4 flex flex-col gap-3">
          <Link
            to="/about"
            className="font-semibold text-base text-left cursor-pointer text-primary-500 hover:text-accent-500"
            onClick={() => setMobileOpen(false)}
          >
            About
          </Link>
          <button
            onClick={() => scrollToSection('books')}
            className="font-semibold text-base text-left cursor-pointer text-primary-500 hover:text-accent-500"
          >
            Books
          </button>
          <button
            onClick={() => scrollToSection('sisters')}
            className="font-semibold text-base text-left cursor-pointer text-primary-500 hover:text-accent-500"
          >
            Meet the Sisters
          </button>
          <button
            onClick={() => scrollToSection('stories')}
            className="font-semibold text-base text-left cursor-pointer text-primary-500 hover:text-accent-500"
          >
            Stories
          </button>
          <Link
            to="/knowledge-base"
            className="font-semibold text-base text-left cursor-pointer text-primary-500 hover:text-accent-500"
            onClick={() => setMobileOpen(false)}
          >
            Knowledge Base
          </Link>
          <a
            href="https://gracie-and-lilys-adventures.myshopify.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-base cursor-pointer text-primary-500 hover:text-accent-500 flex items-center gap-1"
          >
            <span className="w-5 h-5 flex items-center justify-center">
              <i className="ri-store-2-line text-lg"></i>
            </span>
            Shop Merchandise
          </a>
          <a
            href="https://a.co/d/0gs915Vo"
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-base cursor-pointer text-primary-500 hover:text-accent-500 flex items-center gap-1"
          >
            <span className="w-5 h-5 flex items-center justify-center">
              <i className="ri-amazon-fill text-lg"></i>
            </span>
            Shop on Amazon
          </a>
          <a
            href="https://www.etsy.com/shop/GracieandLilysAdv"
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-base cursor-pointer text-primary-500 hover:text-accent-500 flex items-center gap-1"
          >
            <span className="w-5 h-5 flex items-center justify-center">
              <i className="ri-shopping-basket-2-line text-lg"></i>
            </span>
            Shop on Etsy
          </a>
          <a
            href="https://www.facebook.com/profile.php?id=61582630212972"
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-base cursor-pointer text-primary-500 hover:text-accent-500 flex items-center gap-1"
          >
            <span className="w-5 h-5 flex items-center justify-center">
              <i className="ri-facebook-fill text-lg"></i>
            </span>
            Facebook
          </a>
          <button
            onClick={() => onOpenModal()}
            className="bg-accent-500 text-white px-5 py-2.5 rounded-full font-bold text-base whitespace-nowrap cursor-pointer hover:bg-accent-600 transition-colors w-full text-center flex items-center justify-center gap-1.5"
          >
            <span className="w-5 h-5 flex items-center justify-center">
              <i className="ri-gift-line text-lg"></i>
            </span>
            Claim Free Gift
          </button>
          <button
            onClick={() => scrollToSection('books')}
            className="bg-accent-500 text-white px-5 py-2.5 rounded-full font-bold text-base whitespace-nowrap cursor-pointer hover:bg-accent-600 transition-colors w-full text-center"
          >
            Shop Books
          </button>
        </div>
      )}

      <button
        onClick={() => onOpenModal()}
        className={`lg:hidden fixed bottom-6 right-6 z-50 bg-accent-500 text-white px-5 py-3 rounded-full font-bold text-sm whitespace-nowrap cursor-pointer hover:bg-accent-600 transition-all duration-300 flex items-center gap-2 shadow-lg ${
          scrolled ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0 pointer-events-none'
        }`}
      >
        <span className="w-5 h-5 flex items-center justify-center">
          <i className="ri-gift-line text-lg"></i>
        </span>
        <span className="hidden sm:inline">Claim Free Gift</span>
      </button>
    </nav>
  );
}